/**
 * Sanitizes a return URL to prevent open redirect vulnerabilities.
 * Only allows relative URLs that start with "/" and don't contain
 * protocol indicators or other bypass attempts.
 *
 * @param url - The URL to sanitize (may be encoded)
 * @returns A safe relative URL, or "/" if validation fails
 */
export function sanitizeReturnUrl(url: string | null | undefined): string {
  const defaultUrl = "/";

  if (!url || typeof url !== "string") {
    return defaultUrl;
  }

  // Decode the URL if it's encoded
  let decodedUrl: string;
  try {
    decodedUrl = decodeURIComponent(url);
  } catch {
    return defaultUrl;
  }

  // Must start with "/" (relative URL)
  if (!decodedUrl.startsWith("/")) {
    return defaultUrl;
  }

  // Reject protocol-relative URLs (e.g., "//evil.com")
  if (decodedUrl.startsWith("//")) {
    return defaultUrl;
  }

  // Reject URLs that might contain a protocol (e.g., "javascript:", "data:")
  if (decodedUrl.includes(":")) {
    return defaultUrl;
  }

  // Reject URLs with backslashes (potential bypass on some browsers)
  if (decodedUrl.includes("\\")) {
    return defaultUrl;
  }

  return decodedUrl;
}
